import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAccount } from '../context/AccountContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faUserShield, faUserGraduate, faTriangleExclamation, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

export const SubAccountManagement: React.FC = () => {
    const [aliasName, setAliasName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { availableAccounts, setAvailableAccounts } = useAccount();

    useEffect(() => {
        loadAccounts();
    }, []);

    const loadAccounts = async () => {
        try {
            const response = await axios.get('/pool/api/user/aliases');
            setAvailableAccounts(response.data);
        } catch (error) {
            console.error('Failed to load accounts:', error);
        }
    };

    const validateAliasName = (name: string): string | null => {
        if (!name.trim()) {
            return 'Please enter an alias name';
        }

        const validPattern = /^[a-zA-Z0-9_]+$/;
        if (!validPattern.test(name)) {
            return 'Account name can only contain letters, numbers, and underscores';
        }

        if (name.startsWith('_') || name.endsWith('_')) {
            return 'Account name cannot start or end with an underscore';
        }

        if (name.includes('__')) {
            return 'Account name cannot contain consecutive underscores';
        }

        return null;
    };

    const createAlias = async () => {
        const trimmedName = aliasName.trim();
        const validationError = validateAliasName(trimmedName);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const lowercaseName = trimmedName.toLowerCase();
            await axios.post('/pool/api/user/aliases', { aliasName: lowercaseName });
            setAliasName('');
            setSuccess(`Sub-account "${lowercaseName}" created successfully!`);
            await loadAccounts();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create sub-account');
        } finally {
            setLoading(false);
        }
    };

    const aliases = availableAccounts?.filter(acc => acc.isAlias);
    const primaryAccount = availableAccounts.find(acc => acc.isPrimary);

    return (
        <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
                <div>
                    <h2 className="text-xl md:text-2xl font-black text-[#0A3A6B] dark:text-blue-400 tracking-tight flex items-center gap-3">
                        <FontAwesomeIcon icon={faUserShield} className="text-blue-500" />
                        Account Management
                    </h2>
                    <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-1">Manage your primary and sub-accounts</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[9px] md:text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest bg-slate-100 dark:bg-slate-800/50 px-4 py-2 rounded-full border border-slate-200/50 dark:border-slate-700/50">
                        {availableAccounts.length} Total Accounts
                    </span>
                </div>
            </div>

            {/* Create Section */}
            <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl p-6 md:p-8 rounded-[2.5rem] border border-white/40 dark:border-slate-700/50 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500">
                        <FontAwesomeIcon icon={faUserPlus} />
                    </div>
                    <h3 className="text-base md:text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">Create New Sub-Account</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="md:col-span-2">
                        <label className="block text-slate-700 dark:text-slate-300 text-xs font-bold mb-3 uppercase tracking-widest ml-1">
                            Sub-Account Alias
                        </label>
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={aliasName}
                                    onChange={(e) => setAliasName(e.target.value)}
                                    placeholder="e.g., worker_101"
                                    className="w-full bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white rounded-2xl p-4 focus:ring-2 focus:ring-blue-500/50 outline-none transition-all shadow-inner font-mono text-sm"
                                    disabled={loading}
                                />
                            </div>
                            <button
                                onClick={createAlias}
                                disabled={loading || !aliasName.trim()}
                                className="w-[140px] md:w-[160px] h-[52px] bg-gradient-to-r from-[#0A3A6B] to-[#0E4D92] text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:opacity-90 disabled:opacity-50 transition-all shadow-lg shadow-blue-500/20 active:scale-95 whitespace-nowrap flex items-center justify-center"
                            >
                                {loading ? (
                                    <span className="flex items-center gap-2">
                                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                        Creating...
                                    </span>
                                ) : (
                                    'Create Account'
                                )}
                            </button>
                        </div>
                        <p className="mt-3 text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-[0.15em] px-1">
                            Only letters, numbers, and underscores allowed
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mt-6 p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 rounded-2xl text-xs font-bold flex items-center gap-3 animate-in fade-in zoom-in-95">
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/50 text-green-600 dark:text-green-400 rounded-2xl text-xs font-bold flex items-center gap-3 animate-in fade-in zoom-in-95">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {success}
                    </div>
                )}
            </div>

            {/* List Section */}
            <div className="space-y-4">
                <div className="px-2">
                    <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em]">Active Accounts</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Primary Account Card */}
                    {primaryAccount && (
                        <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-6 rounded-[2.5rem] shadow-xl relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl group-hover:scale-110 transition-transform"></div>

                            <div className="relative flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white shadow-inner">
                                        <FontAwesomeIcon icon={faUserGraduate} className="text-2xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg md:text-xl font-black text-white tracking-tight">{primaryAccount.userName}</h4>
                                        <span className="inline-block px-3 py-1 bg-white/20 text-white rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest mt-1 backdrop-blur-sm">
                                            Primary Account
                                        </span>
                                    </div>
                                </div>
                                <div className="text-white/40">
                                    <FontAwesomeIcon icon={faUserShield} className="text-3xl" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Alias Cards */}
                    {aliases.map(account => (
                        <div key={account.userName} className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl p-6 rounded-[2.5rem] border border-white/40 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-300">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-2xl flex items-center justify-center text-slate-400 dark:text-slate-500">
                                        <FontAwesomeIcon icon={faUserPlus} className="text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg md:text-xl font-black text-slate-800 dark:text-white tracking-tight">{account.userName}</h4>
                                        <span className="inline-block px-3 py-1 bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest mt-1">
                                            Sub-Account
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {aliases.length === 0 && (
                        <div className="col-span-full md:col-span-1 p-8 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-[2.5rem] flex flex-col items-center justify-center text-center opacity-60">
                            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400 mb-3">
                                <FontAwesomeIcon icon={faUserPlus} />
                            </div>
                            <p className="text-sm font-bold text-slate-500 uppercase tracking-widest leading-relaxed">No sub-accounts found</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
